/**
 * contractAddons.ts
 * Central library for managing contract add-ons via `contract_addons` table.
 *
 * FLOW:
 *  1. activateAddonFull()  → insert contract_addons + contract_charges + client_addons sync + side effects
 *  2. updateAddonStatus()  → update contract_addons + sync client_addons + deactivate charges if cancelled
 *  3. Side effects fire real entity creation (digital_invitation, signature_book, etc.)
 */

import { supabase } from "@/lib/supabaseClient";
import { createNotification } from "@/lib/createNotification";

export type ActivationMode = "paid" | "gift" | "promo";
export type AddonStatus    = "active" | "disabled" | "cancelled";

export interface ContractAddon {
  id: string;
  tenant_id: string;
  contract_id: string;
  client_id: string;
  addon_key: string;
  activation_mode: ActivationMode;
  price: number;
  status: AddonStatus;
  promo_code: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AddonActivationPayload {
  tenantId: string;
  contractId: string;
  clientId: string;
  clientName?: string;
  addonKey: string;
  mode: ActivationMode;
  price?: number;
  promoCode?: string;
  notes?: string;
  /** Optional contract metadata for richer side effects */
  contractMeta?: {
    event_date?: string;
    event_location?: string;
    title?: string;
  };
}

/* ─────────────────────────────────────────────
   ADDON CATALOG — 8 supported add-ons
───────────────────────────────────────────── */

export interface AddonCatalogItem {
  key: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  bg: string;
  accentBorder: string;
  category: "digital" | "production" | "service";
  defaultPrice: number;
  moduleKey?: string;
}

export const ADDON_CATALOG: AddonCatalogItem[] = [
  {
    key:          "digital_invitation",
    label:        "Invitación Digital",
    description:  "Invitación interactiva con RSVP, cuenta regresiva, música y galería de fotos.",
    icon:         "ri-mail-open-line",
    color:        "text-[#C8A96E]",
    bg:           "bg-[#C8A96E]/10",
    accentBorder: "border-[#C8A96E]/30",
    category:     "digital",
    defaultPrice: 50,
    moduleKey:    "digital_invitations",
  },
  {
    key:          "signature_book",
    label:        "Libro de Firmas",
    description:  "Libro digital con mensajes sobre fotos, QR para invitados y PDF descargable.",
    icon:         "ri-book-open-line",
    color:        "text-amber-600",
    bg:           "bg-amber-50",
    accentBorder: "border-amber-200",
    category:     "digital",
    defaultPrice: 80,
    moduleKey:    "signature_book",
  },
  {
    key:          "media_packages",
    label:        "Paquete de Medios",
    description:  "Álbumes impresos, canvas, USB personalizado y productos físicos del evento.",
    icon:         "ri-image-2-line",
    color:        "text-rose-500",
    bg:           "bg-rose-50",
    accentBorder: "border-rose-200",
    category:     "production",
    defaultPrice: 200,
  },
  {
    key:          "dvd_online",
    label:        "Video Online / DVD",
    description:  "Edición de video del evento con entrega en streaming privado o USB/DVD.",
    icon:         "ri-film-line",
    color:        "text-violet-500",
    bg:           "bg-violet-50",
    accentBorder: "border-violet-200",
    category:     "production",
    defaultPrice: 150,
  },
  {
    key:          "photo_selection",
    label:        "Selección de Fotos",
    description:  "Portal privado para que el cliente seleccione sus fotos favoritas del evento.",
    icon:         "ri-gallery-line",
    color:        "text-sky-500",
    bg:           "bg-sky-50",
    accentBorder: "border-sky-200",
    category:     "digital",
    defaultPrice: 0,
  },
  {
    key:          "final_deliveries",
    label:        "Entrega Final",
    description:  "Entrega digital de fotos editadas en alta resolución con acceso al portal.",
    icon:         "ri-send-plane-line",
    color:        "text-emerald-600",
    bg:           "bg-emerald-50",
    accentBorder: "border-emerald-200",
    category:     "production",
    defaultPrice: 0,
  },
  {
    key:          "appointments",
    label:        "Citas y Reuniones",
    description:  "Agendado de citas online: pruebas, entregas, consultas previas al evento.",
    icon:         "ri-calendar-check-line",
    color:        "text-indigo-500",
    bg:           "bg-indigo-50",
    accentBorder: "border-indigo-200",
    category:     "service",
    defaultPrice: 0,
  },
  {
    key:          "ai_sales_assistant",
    label:        "Asistente IA de Ventas",
    description:  "Agente de IA personalizado que responde consultas y captura leads automáticamente.",
    icon:         "ri-robot-line",
    color:        "text-teal-600",
    bg:           "bg-teal-50",
    accentBorder: "border-teal-200",
    category:     "service",
    defaultPrice: 30,
  },
];

export const ADDON_MAP: Record<string, AddonCatalogItem> = Object.fromEntries(
  ADDON_CATALOG.map((a) => [a.key, a]),
);

export function getAddonMeta(
  key: string,
): Pick<AddonCatalogItem, "label" | "icon" | "color" | "bg" | "accentBorder"> {
  return (
    ADDON_MAP[key] ?? {
      label:        key,
      icon:         "ri-puzzle-line",
      color:        "text-slate-500",
      bg:           "bg-slate-100",
      accentBorder: "border-slate-200",
    }
  );
}

export const CATEGORY_META: Record<string, { label: string; icon: string }> = {
  digital:    { label: "Digital",    icon: "ri-smartphone-line" },
  production: { label: "Producción", icon: "ri-camera-line" },
  service:    { label: "Servicio",   icon: "ri-service-line" },
};

/* ─────────────────────────────────────────────
   QUERIES
───────────────────────────────────────────── */

export async function getContractAddons(
  tenantId: string,
  contractId: string,
): Promise<ContractAddon[]> {
  const { data, error } = await supabase
    .from("contract_addons")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("contract_id", contractId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("[contractAddons] getContractAddons:", error.message);
    return [];
  }
  return (data ?? []) as ContractAddon[];
}

export async function getClientActiveAddonKeys(
  tenantId: string,
  clientId: string,
): Promise<string[]> {
  const { data } = await supabase
    .from("contract_addons")
    .select("addon_key")
    .eq("tenant_id", tenantId)
    .eq("client_id", clientId)
    .eq("status", "active");
  return (data ?? []).map((r: { addon_key: string }) => r.addon_key);
}

export async function clientHasAddon(
  tenantId: string,
  clientId: string,
  addonKey: string,
): Promise<boolean> {
  const { data } = await supabase
    .from("contract_addons")
    .select("id")
    .eq("tenant_id", tenantId)
    .eq("client_id", clientId)
    .eq("addon_key", addonKey)
    .eq("status", "active")
    .maybeSingle();
  return !!data;
}

export async function contractHasAddon(
  tenantId: string,
  contractId: string,
  addonKey: string,
): Promise<boolean> {
  const { data } = await supabase
    .from("contract_addons")
    .select("id")
    .eq("tenant_id", tenantId)
    .eq("contract_id", contractId)
    .eq("addon_key", addonKey)
    .maybeSingle();
  return !!data;
}

/* ─────────────────────────────────────────────
   CLIENT ADDONS SYNC
   Keeps `client_addons` in sync so resolveClientFeatures
   can read from a single source (both tables merged).
───────────────────────────────────────────── */

async function syncClientAddon(
  tenantId: string,
  clientId: string,
  contractId: string,
  addonKey: string,
  status: string,
  price: number,
): Promise<void> {
  try {
    // Upsert into client_addons to keep the feature map in sync
    const { data: existing } = await supabase
      .from("client_addons")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .eq("addon_key", addonKey)
      .eq("contract_id", contractId)
      .maybeSingle();

    if (existing) {
      await supabase
        .from("client_addons")
        .update({ status, price, updated_at: new Date().toISOString() })
        .eq("id", existing.id);
    } else {
      await supabase.from("client_addons").insert({
        tenant_id:   tenantId,
        client_id:   clientId,
        contract_id: contractId,
        addon_key:   addonKey,
        status,
        price,
        source:      "contract",
      });
    }
  } catch (e) {
    // Non-critical — resolveClientFeatures reads both tables anyway
    console.warn("[contractAddons] syncClientAddon:", e);
  }
}

/* ─────────────────────────────────────────────
   CORE: insert + charge + sync
───────────────────────────────────────────── */

export async function activateContractAddon(
  payload: AddonActivationPayload,
): Promise<{ success: boolean; addonId?: string; error?: string }> {
  const {
    tenantId, contractId, clientId, clientName,
    addonKey, mode, price = 0, promoCode, notes,
  } = payload;

  const alreadyExists = await contractHasAddon(tenantId, contractId, addonKey);
  if (alreadyExists) return { success: false, error: "addon_already_exists" };

  const meta       = getAddonMeta(addonKey);
  const finalPrice = mode === "paid" ? price : 0;

  /* 1. Insert contract_addon */
  const { data: inserted, error: insertErr } = await supabase
    .from("contract_addons")
    .insert({
      tenant_id:       tenantId,
      contract_id:     contractId,
      client_id:       clientId,
      addon_key:       addonKey,
      activation_mode: mode,
      price:           finalPrice,
      status:          "active",
      promo_code:      mode === "promo" ? (promoCode ?? null) : null,
      notes:           notes ?? (mode === "promo" ? `Promo: ${promoCode}` : null),
    })
    .select()
    .maybeSingle();

  if (insertErr || !inserted) {
    console.error("[contractAddons] insert:", insertErr?.message);
    return { success: false, error: insertErr?.message ?? "insert_failed" };
  }

  /* 2. Create contract_charge if paid */
  if (mode === "paid" && finalPrice > 0) {
    const chargeTitle = `${meta.label} — Activación`;
    const { data: existingCharge } = await supabase
      .from("contract_charges")
      .select("id")
      .eq("contract_id", contractId)
      .eq("title", chargeTitle)
      .maybeSingle();

    if (!existingCharge) {
      await supabase.from("contract_charges").insert({
        tenant_id:   tenantId,
        contract_id: contractId,
        client_id:   clientId,
        title:       chargeTitle,
        amount:      finalPrice,
        status:      "active",
        source:      "addon",
      });
    }
  }

  /* 3. Sync client_addons */
  await syncClientAddon(tenantId, clientId, contractId, addonKey, "active", finalPrice);

  /* 4. Notify admin */
  const modeLabel =
    mode === "paid"  ? `cobrado ($${finalPrice})` :
    mode === "gift"  ? "regalado" :
    `activado con promo "${promoCode ?? ""}"`;

  createNotification({
    tenantId,
    type:    "success",
    title:   `${meta.label} activado`,
    message: `El add-on fue ${modeLabel}${clientName ? ` para ${clientName}` : ""}.`,
  });

  return { success: true, addonId: (inserted as ContractAddon).id };
}

/* ─────────────────────────────────────────────
   UPDATE STATUS (with charge + client_addons sync)
───────────────────────────────────────────── */

export async function updateAddonStatus(
  tenantId: string,
  addonId: string,
  status: AddonStatus,
): Promise<boolean> {
  const { data: addonRow, error: fetchErr } = await supabase
    .from("contract_addons")
    .select("id, tenant_id, contract_id, client_id, addon_key, activation_mode, price")
    .eq("id", addonId)
    .eq("tenant_id", tenantId)
    .maybeSingle();

  if (fetchErr || !addonRow) return false;

  const { error } = await supabase
    .from("contract_addons")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", addonId)
    .eq("tenant_id", tenantId);

  if (error) return false;

  /* Sync client_addons */
  const clientAddonStatus = status === "active" ? "active" : "inactive";
  await syncClientAddon(
    tenantId,
    addonRow.client_id,
    addonRow.contract_id,
    addonRow.addon_key,
    clientAddonStatus,
    Number(addonRow.price),
  );

  /* If cancelled: deactivate corresponding contract_charge */
  if (status === "cancelled" && addonRow.activation_mode === "paid") {
    const meta         = getAddonMeta(addonRow.addon_key);
    const chargeTitle  = `${meta.label} — Activación`;

    await supabase
      .from("contract_charges")
      .update({ status: "inactive", updated_at: new Date().toISOString() })
      .eq("contract_id", addonRow.contract_id)
      .eq("tenant_id", tenantId)
      .eq("title", chargeTitle);
  }

  /* If re-activated: re-enable contract_charge */
  if (status === "active" && addonRow.activation_mode === "paid" && Number(addonRow.price) > 0) {
    const meta        = getAddonMeta(addonRow.addon_key);
    const chargeTitle = `${meta.label} — Activación`;

    await supabase
      .from("contract_charges")
      .update({ status: "active", updated_at: new Date().toISOString() })
      .eq("contract_id", addonRow.contract_id)
      .eq("tenant_id", tenantId)
      .eq("title", chargeTitle);
  }

  return true;
}

/* ─────────────────────────────────────────────
   SIDE EFFECTS — entity creation on activation
───────────────────────────────────────────── */

export interface AddonSideEffectPayload {
  tenantId: string;
  contractId: string;
  clientId: string;
  clientName?: string;
  mode: ActivationMode;
  price?: number;
  promoCode?: string;
  contractMeta?: {
    event_date?: string;
    event_location?: string;
    title?: string;
  };
}

/** digital_invitation → create digital_invitations row with contract data */
export async function sideEffectDigitalInvitation(p: AddonSideEffectPayload): Promise<void> {
  const { data: existing } = await supabase
    .from("digital_invitations")
    .select("id")
    .eq("tenant_id", p.tenantId)
    .eq("client_id", p.clientId)
    .eq("contract_id", p.contractId)
    .maybeSingle();

  if (!existing) {
    const slug      = `inv-${p.clientId.slice(0, 8)}-${Date.now().toString(36)}`;
    const eventDate = p.contractMeta?.event_date;
    const eventTitle = p.contractMeta?.title ?? "Nueva Invitación";

    await supabase.from("digital_invitations").insert({
      tenant_id:     p.tenantId,
      client_id:     p.clientId,
      contract_id:   p.contractId,
      title:         eventTitle,
      event_type:    "Wedding",
      event_name:    eventTitle,
      event_location: p.contractMeta?.event_location ?? null,
      event_date:    eventDate ? new Date(eventDate).toISOString() : null,
      public_slug:   slug,
      design_mode:   "template",
      is_published:  false,
      rsvp_enabled:  true,
    });
  }
}

/** signature_book → create signature_books row */
export async function sideEffectSignatureBook(p: AddonSideEffectPayload): Promise<void> {
  const { data: existing } = await supabase
    .from("signature_books")
    .select("id")
    .eq("tenant_id", p.tenantId)
    .eq("client_id", p.clientId)
    .eq("contract_id", p.contractId)
    .maybeSingle();

  if (!existing) {
    const slug = `book-${p.clientId.slice(0, 8)}-${Date.now().toString(36)}`;
    const now  = new Date();
    const exp  = new Date(now);
    exp.setMonth(exp.getMonth() + 6);

    await supabase.from("signature_books").insert({
      tenant_id:          p.tenantId,
      client_id:          p.clientId,
      contract_id:        p.contractId,
      title:              `Libro de Firmas — ${p.clientName ?? "Cliente"}`,
      status:             "draft",
      guest_book_enabled: true,
      public_slug:        slug,
      event_date:         p.contractMeta?.event_date ?? null,
      activated_at:       now.toISOString(),
      expires_at:         exp.toISOString(),
    });
  } else {
    // Re-enable if it was disabled
    await supabase
      .from("signature_books")
      .update({ guest_book_enabled: true, updated_at: new Date().toISOString() })
      .eq("id", existing.id);
  }
}

/** photo_selection → initialize jobs to not_started if unset */
export async function sideEffectPhotoSelection(p: AddonSideEffectPayload): Promise<void> {
  const { data: jobs } = await supabase
    .from("jobs")
    .select("id, photo_selection_status")
    .eq("tenant_id", p.tenantId)
    .eq("client_id", p.clientId);

  if (!jobs || jobs.length === 0) return;

  const jobsToInit = jobs.filter(
    (j: { id: string; photo_selection_status: string | null }) =>
      !j.photo_selection_status || j.photo_selection_status === "",
  );

  if (jobsToInit.length > 0) {
    await supabase
      .from("jobs")
      .update({ photo_selection_status: "not_started", updated_at: new Date().toISOString() })
      .in("id", jobsToInit.map((j: { id: string }) => j.id));
  }
}

/**
 * final_deliveries — portal section visibility controlled by addonAccessGuard.
 * No entity creation needed.
 */
export async function sideEffectFinalDeliveries(_p: AddonSideEffectPayload): Promise<void> {
  // Deliveries are created by admin in job detail — no entity needed here.
}

/**
 * media_packages / dvd_online — portal media access.
 * No entity creation needed.
 */
export async function sideEffectMediaAccess(_p: AddonSideEffectPayload): Promise<void> {
  // Media packages created by admin separately.
}

/**
 * appointments — portal appointments access.
 * No entity creation needed.
 */
export async function sideEffectAppointments(_p: AddonSideEffectPayload): Promise<void> {
  // Appointments created by admin with show_in_portal=true.
}

/**
 * ai_sales_assistant — tenant-level feature, no portal section.
 */
export async function sideEffectAiAssistant(_p: AddonSideEffectPayload): Promise<void> {
  // No portal section.
}

/** Map addon key → side effect function */
export const ADDON_SIDE_EFFECTS: Record<
  string,
  (p: AddonSideEffectPayload) => Promise<void>
> = {
  digital_invitation: sideEffectDigitalInvitation,
  signature_book:     sideEffectSignatureBook,
  photo_selection:    sideEffectPhotoSelection,
  final_deliveries:   sideEffectFinalDeliveries,
  media_packages:     sideEffectMediaAccess,
  dvd_online:         sideEffectMediaAccess,
  appointments:       sideEffectAppointments,
  ai_sales_assistant: sideEffectAiAssistant,
};

/* ─────────────────────────────────────────────
   FULL PIPELINE: activate + side effect
───────────────────────────────────────────── */

/**
 * Complete addon activation:
 *  1. Insert contract_addon row
 *  2. Create contract_charge (if paid)
 *  3. Sync client_addons
 *  4. Run side effect (create entity if needed)
 */
export async function activateAddonFull(
  payload: AddonActivationPayload & { clientName?: string },
): Promise<{ success: boolean; error?: string }> {
  const result = await activateContractAddon(payload);
  if (!result.success) return result;

  const sideEffect = ADDON_SIDE_EFFECTS[payload.addonKey];
  if (sideEffect) {
    await sideEffect({
      tenantId:     payload.tenantId,
      contractId:   payload.contractId,
      clientId:     payload.clientId,
      clientName:   payload.clientName,
      mode:         payload.mode,
      price:        payload.price,
      promoCode:    payload.promoCode,
      contractMeta: payload.contractMeta,
    });
  }

  return { success: true };
}